import { Component, HostListener, OnInit } from '@angular/core';
import { UserService } from '../core/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../core/auth.service';
import { appVariables } from '../app.constants';
import { ApplicationInsightsService } from '../shared/application-insights.service';
import { IUser } from "../core/interfaces/app.interface";
import { MatSnackBar } from '@angular/material/snack-bar';
import { MsalService } from '@azure/msal-angular';
import { map, switchMap } from 'rxjs/operators';
import { FeatureFlagService } from 'src/betauser-flag.service';

@Component({
  selector: 'app-root',
  template: `
    <app-switch-groups *ngIf="loadSwitchUserGroup" (userGroup)="authenticateUser($event.isTM, $event.email)"></app-switch-groups>
    <app-spinner *ngIf="isBusy" [isbusy]="isBusy"></app-spinner>
    <router-outlet *ngIf="!loadSwitchUserGroup"></router-outlet>`
})
export class AppComponent implements OnInit {

  loadSwitchUserGroup = false;
  isBusy = false;

  @HostListener('document:click', ['$event'])
  clickout(event) {
    this.closeAlert();
  }

  constructor(
    public msalService: MsalService,
    private authService: AuthService,
    private userService: UserService,
    private featureFlagService: FeatureFlagService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private applicationInsightsService: ApplicationInsightsService
  ) { }

  ngOnInit(): void {
    if (window.location.hash.includes('shipto')) { // Replaced lodash
      this.route.paramMap.subscribe(params => {
        const shipToSearchValue = params.get('shipto');
        const countrySearchValue = this.route.snapshot.queryParamMap.get('country');
        if (shipToSearchValue && countrySearchValue) {
          sessionStorage.setItem(appVariables.guids.paramGUID, shipToSearchValue);
          sessionStorage.setItem(appVariables.guids.searchGUID, countrySearchValue);
          this.router.navigate(['']);
        }
      });
    }

    if (window === window.parent) {
      if (this.msalService.instance.getAllAccounts()[0]?.username && !this.userService.isAuthenticatingUser) {
        this.applicationInsightsService.setUserId(this.msalService.instance.getAllAccounts()[0]?.username);
        if (sessionStorage.getItem(appVariables.guids.paramGUID) && sessionStorage.getItem(appVariables.guids.searchGUID)) {
          this.authenticateUser(false);
        } else if (appVariables.adminDevGroup.includes(this.msalService.instance.getAllAccounts()[0]?.username.toLowerCase())) { // Replaced lodash
          this.router.navigate(['']);
          this.loadSwitchUserGroup = true;
        } else {
          this.authenticateUser(false);
        }
      }
    }
  }
  

  authenticateUser(customSwitch: boolean, customSwitchEmail?: string): void {

    const currentAccounts = this.msalService.instance.getAllAccounts();
    const logInEmailAs = customSwitch ? customSwitchEmail! : currentAccounts[0]?.username;

    sessionStorage.setItem("username", currentAccounts[0].name ?? "");
    this.applicationInsightsService.setUserId(currentAccounts[0]?.username);

    this.isBusy = true;
    this.loadSwitchUserGroup = false;
    setTimeout(() => {
        if (this.isBusy) {
            this.isBusy = false;
           
        }
    }, 2000);

    this.featureFlagService.getConfig().subscribe({
      next: (config) => {

        if (config?.betaUserEnabled === true) {
          console.log('=== BETA ENABLED PATH ===');

          // --- THIS IS THE UPDATED LOGIC ---
          // 1. Call our new backend API to check beta status
          this.authService.checkBetaUser(logInEmailAs).pipe(
            switchMap(betaStatus => {
              // betaStatus is { isBetaUser: boolean }
              const emailExists = betaStatus.isBetaUser;

              // 2. Call the original authenticateUser (sales-alignment)
              return this.authService.authenticateUser(logInEmailAs, customSwitch).pipe(
                map(authResponse => {
                  console.log('Auth response:', authResponse);
                  // 3. Pass both results along
                  return { emailExists, authResponse };
                })
              );
            })
          ).subscribe({
            next: ({ emailExists }) => {
              // ==================== BETA USER FLAG = TRUE ====================
              // Scenario 1: Email EXISTS in beta list → navigate to /orderStatus
              // Scenario 2: Email NOT in beta list → check territories
              //   - If territories > 0 → navigate to /territoryView
              //   - Else → navigate to /nonTerritoryDashboard
              
              this.isBusy = false;
              this.userService.currentUser.email = logInEmailAs.toLowerCase();
              
              if (emailExists) {
                // Email exists in beta list → navigate based on territories
                console.log('Email exists in beta list');
                const hasTerritories = this.userService.currentUser?.territories?.length > 0;
                console.log('Number of territories:', this.userService.currentUser?.territories?.length);
                
                const betatargetRoute = hasTerritories ? '/territoryView' : '/nonTerritoryDashboard';
                console.log('Email exists, navigating to:', betatargetRoute);
                
                this.router.navigate([betatargetRoute]).then(
                  (success) => console.log('Navigation success:', success),
                  (error) => console.error('Navigation error:', error)
                 
                );
              } else {
                // Email NOT in beta list → check territories
                const hasTerritories = this.userService.currentUser?.territories?.length > 0;
                console.log('Number of territories:', this.userService.currentUser?.territories?.length);
                
                const betatargetRoute = hasTerritories ? '/orderStatus' : '/nonTerritoryDashboard';
                console.log('Email not in beta list, navigating to:', betatargetRoute);
                
                this.router.navigate([betatargetRoute]).then(
                  (success) => console.log('Navigation success:', success),
                  (error) => console.error('Navigation error:', error)
                );
              }
            },
            error: (error) => {
              console.error('Beta flow error:', error);
              this.isBusy = false;
              this.router.navigate(['/nonTerritoryDashboard']);
            }
          });
          // --- END OF UPDATED LOGIC ---

        } else {
          console.log('=== BETA DISABLED PATH ===');
          // When beta is disabled, ALL users go to nonTerritoryDashboard

          this.authService.authenticateUser(logInEmailAs, customSwitch).subscribe({
            next: (authResponse) => {
              console.log('Auth successful, response:', authResponse);

              this.isBusy = false;
              this.userService.currentUser.email = logInEmailAs.toLowerCase();
              
              const hasTerritories = this.userService.currentUser?.territories?.length > 0;
              console.log('Number of territories:', this.userService.currentUser?.territories?.length);
              
              const betatargetRoute = hasTerritories ? '/territoryView' : '/nonTerritoryDashboard';
              console.log('Navigating to:', betatargetRoute);
              
              this.router.navigate([betatargetRoute]).then(
                (success) => console.log('Navigation success:', success),
                (error) => console.error('Navigation error:', error)
              );
            },
            error: (error) => {
              console.error('Authentication error:', error);
              this.isBusy = false;
              //this.snackBar.open("Something went wrong. Please try again later.", 'Close');
              this.router.navigate(['/nonTerritoryDashboard']).then(
                (success) => console.log('Error navigation success:', success),
                (error) => console.error('Error navigation failed:', error)
              );
            }
          });
        }
      },
      error: (error) => {
        console.error('Feature flag config error:', error);
        this.isBusy = false;
        this.snackBar.open("Configuration error. Please try again later.", 'Close');

        // Fallback authentication - always go to nonTerritoryDashboard on config error
        this.authService.authenticateUser(logInEmailAs, customSwitch).subscribe({
          next: () => {
            console.log('Fallback auth successful, navigating to /nonTerritoryDashboard');
            this.userService.currentUser.email = logInEmailAs.toLowerCase();
            this.router.navigate(['/nonTerritoryDashboard']);
          },
          error: (authError) => {
            console.error('Fallback auth error:', authError);
            this.router.navigate(['/nonTerritoryDashboard']);
          }
        });
      }
    });
  }

  closeAlert() {
    if (this.snackBar._openedSnackBarRef) {
      this.snackBar.dismiss();
    }
  }
}

